import { type OrgData, type OrgLabels, type OrgBusinessRecord } from './organisationFixtures';
import { fetchBusinessRecords, fetchOrganisations } from './organisationHttpClient';
import type { BusinessRecordDetailsDto, OrgDetailsDto } from './organisationApiTypes';

const REGION_LABELS: Record<string, OrgLabels> = {
  AU: {
    number: 'ABN/Business number',
    eft: 'PAYG EFT code',
    withholding: 'PAYG witholding (days)',
    reportingTitle: 'STP Reporting',
    payrollTitle: 'Payrolls',
    bmsId: 'BMS ID',
    branch2: 'ATO branch',
    ssid: 'SSID (Sender)',
  },
  GB: {
    number: 'Company number',
    eft: 'PAYE reference',
    withholding: 'Tax period (week)',
    reportingTitle: 'RTI / HMRC',
    payrollTitle: 'Payrolls',
    bmsId: 'Accounts Office ref',
    branch2: 'HMRC office no.',
    ssid: 'Sender ID',
  },
  NZ: {
    number: 'NZBN',
    eft: 'IRD number',
    withholding: 'Pay period (days)',
    reportingTitle: 'Payday filing',
    payrollTitle: 'Payrolls',
    bmsId: 'myIR account',
    branch2: 'IR dept',
    ssid: 'Gateway ID',
  },
};

const DEFAULT_LABELS: OrgLabels = REGION_LABELS.AU;

const REGION_COLORS: Record<string, string> = {
  AU: '#a02050',
  GB: '#1e4d8b',
  NZ: '#065f46',
};

const deriveBadge = (tradingName?: string, id?: string): string => {
  if (tradingName) {
    const initials = tradingName.split(/\s+/).map((w) => w[0]?.toUpperCase() ?? '').join('');
    return initials.slice(0, 3) || (id?.slice(0, 3).toUpperCase() ?? '?');
  }
  return id?.slice(0, 3).toUpperCase() ?? '?';
};

const mapBusinessRecord = (record: BusinessRecordDetailsDto, regionCode: string): OrgBusinessRecord => ({
  businessRecordId: record.businessRecordId,
  status: record.status,
  defaultForNewEmployments: record.defaultForNewEmployments,
  name: record.tradingName ?? record.legalName ?? '—',
  badge: deriveBadge(record.tradingName, record.businessRecordId),
  legal: {
    country: regionCode,
    number: record.abn ?? '—',
    branch: record.abnBranch ?? '—',
    legalName: record.legalName ?? '—',
    tradingName: record.tradingName ?? '—',
    taxSignatory: '—',
    eft: '—',
    withholding: '—',
    regionalTax: false,
  },
  stp: {
    bmsId: '—',
    reportingParty: '—',
    branch2: '—',
    ssid: '—',
    phase: '—',
    lastSubmission: '—',
    tone: 'brand',
  },
  payrolls: [],
});

export const mapOrgDetailsToOrgData = (org: OrgDetailsDto, businessRecords: BusinessRecordDetailsDto[] = []): OrgData => ({
  label: org.displayName ?? org.id ?? '—',
  badge: deriveBadge(org.displayName, org.id),
  color: REGION_COLORS[org.regionCode ?? ''] ?? '#505050',
  labels: REGION_LABELS[org.regionCode ?? ''] ?? DEFAULT_LABELS,
  overview: {
    identity: {
      name: org.displayName ?? '—',
      shortCode: org.id?.slice(0, 8) ?? '—',
      country: org.regionCode ?? '',
      fyEnd: org.financialYearEndBasis ?? '—',
      hqAddress: '—',
    },
    contact: {
      primary: org.primaryContact?.name ?? '—',
      phone: org.primaryContact?.phone ?? '—',
      supportEmail: org.primaryContact?.email ?? '—',
      senderEmail: '—',
    },
    defaults: {
      timezone: org.defaultTimezone ?? '',
      currency: org.defaultCurrency ?? '',
    },
    callout: { icon: 'info', tone: 'brand', title: 'Organisation details', text: '' },
  },
  businessRecords: businessRecords.map((record) => mapBusinessRecord(record, org.regionCode ?? '')),
});

export const listOrganisations = async (): Promise<{ orgIds: string[]; orgs: Record<string, OrgData> }> => {
  const response = await fetchOrganisations();
  const dtos = response.result ?? [];
  const orgs: Record<string, OrgData> = {};
  const orgIds: string[] = [];
  for (const dto of dtos) {
    if (!dto.id) continue;
    const businessRecords = (await fetchBusinessRecords(dto.id)).result ?? [];
    orgs[dto.id] = mapOrgDetailsToOrgData(dto, businessRecords);
    orgIds.push(dto.id);
  }
  return { orgIds, orgs };
};
